import { useEffect, useState } from "react";
import api from "@/lib/api";
import { CHALLENGE_TEMPLATES, CHALLENGE_CATEGORIES } from "@/data/parentingData";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus, Trophy, Flame, Trash2, CheckCircle2, Sparkles } from "lucide-react";
import SouvenirCard from "@/components/SouvenirCard";
import { useAuth } from "@/context/AuthContext";
import Mascot from "@/components/Mascot";
import { randomDayCheer } from "@/lib/cheers";

export default function ChallengesPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [days, setDays] = useState(7);
  const [category, setCategory] = useState("Tous");
  const templates =
    category === "Tous" ? CHALLENGE_TEMPLATES : CHALLENGE_TEMPLATES.filter((t) => t.category === category);
  const [souvenirChallenge, setSouvenirChallenge] = useState(null);
  const [souvenirSeen, setSouvenirSeen] = useState(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem("parentia_souvenir_seen") || "[]"));
    } catch {
      return new Set();
    }
  });

  const load = async () => {
    const { data } = await api.get("/challenges");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const applyTemplate = (t) => {
    setTitle(t.title); setDescription(t.description); setDays(t.duration_days); setOpen(true);
  };

  const create = async (e) => {
    e.preventDefault();
    await api.post("/challenges", { title, description, duration_days: parseInt(days, 10) });
    toast.success("Défi lancé 💪");
    setTitle(""); setDescription(""); setDays(7); setOpen(false);
    load();
  };

  const check = async (id, day) => {
    const { data } = await api.post(`/challenges/${id}/check`, { day });
    setItems((arr) => arr.map((c) => (c.id === id ? data : c)));
    const finished = data.completed_days.length === data.duration_days;
    if (finished && !souvenirSeen.has(data.id)) {
      const next = new Set(souvenirSeen);
      next.add(data.id);
      setSouvenirSeen(next);
      localStorage.setItem("parentia_souvenir_seen", JSON.stringify([...next]));
      setSouvenirChallenge(data);
    } else if (!finished && data.completed_days.includes(day)) {
      toast.success(randomDayCheer());
    }
  };

  const showSouvenir = (c) => setSouvenirChallenge(c);

  const remove = async (id) => {
    await api.delete(`/challenges/${id}`);
    toast.success("Défi supprimé");
    load();
  };

  return (
    <div className="space-y-8" data-testid="family-challenges-section">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Mascot pose="trophy" size={96} anim="wiggle" className="hidden sm:block shrink-0 mascot-pop" />
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-[#16233D]" style={{ fontFamily: "Outfit, sans-serif" }}>Défis famille</h1>
            <p className="mt-2 text-[#77849C]">Des petits engagements pour de grands moments.</p>
          </div>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button data-testid="new-challenge-btn" className="brand-btn rounded-full px-5 py-2 flex items-center gap-2">
              <Plus className="h-4 w-4" /> Nouveau défi
            </button>
          </DialogTrigger>
          <DialogContent className="rounded-2xl">
            <DialogHeader><DialogTitle>Créer un défi</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-3">
              <div><Label>Titre</Label><Input data-testid="ch-title" value={title} onChange={(e) => setTitle(e.target.value)} required /></div>
              <div><Label>Description</Label><Textarea data-testid="ch-desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={3} required /></div>
              <div><Label>Durée (jours)</Label><Input data-testid="ch-days" type="number" min="1" max="30" value={days} onChange={(e) => setDays(e.target.value)} required /></div>
              <Button data-testid="ch-save" type="submit" className="brand-btn w-full">Lancer</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <section>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <p className="text-xs font-bold uppercase tracking-wider text-[#77849C]">
            Idées prêtes — {CHALLENGE_TEMPLATES.length} défis à faire en famille
          </p>
          <span className="text-sm text-[#77849C]" data-testid="tpl-count">
            {templates.length} idée{templates.length > 1 ? "s" : ""}
          </span>
        </div>
        <div className="filter-row mb-4" data-testid="tpl-category-filters">
          {["Tous", ...CHALLENGE_CATEGORIES].map((c) => (
            <button
              key={c}
              data-testid={`tpl-cat-${c}`}
              onClick={() => setCategory(c)}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors duration-200 ${
                category === c
                  ? "bg-[#23375E] text-white"
                  : "bg-white border border-[#E3E8F1] text-[#44506B] hover:border-[#23375E]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {templates.map((t) => (
            <button key={t.title} data-testid={`tpl-${t.title.substring(0,10)}`} onClick={() => applyTemplate(t)}
              className="soft-card p-4 text-left hover:border-[#23375E] transition-colors duration-200">
              <p className="font-semibold text-[#16233D]">{t.title}</p>
              <p className="mt-1 text-sm text-[#77849C] line-clamp-3">{t.description}</p>
              <div className="mt-2 flex items-center justify-between gap-2">
                <span className="text-xs text-[#23375E] font-medium">{t.duration_days} jour{t.duration_days > 1 ? "s" : ""}</span>
                <span className="age-badge pastel-butter">{t.ages}</span>
              </div>
            </button>
          ))}
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="text-xl font-semibold text-[#16233D]">Mes défis en cours</h2>
        {items.length === 0 && <p className="text-[#77849C]">Aucun défi pour le moment. Choisis une idée ci-dessus !</p>}
        {items.map((c) => {
          const days = Array.from({ length: c.duration_days }, (_, i) => i);
          const streak = c.completed_days.length;
          const pct = (streak / c.duration_days) * 100;
          return (
            <article key={c.id} data-testid={`challenge-${c.id}`} className="soft-card p-6">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 text-[#E8899F] text-xs font-bold uppercase tracking-wider">
                    <Trophy className="h-4 w-4" /> Défi
                  </div>
                  <h3 className="mt-1 text-xl font-semibold text-[#16233D]">{c.title}</h3>
                  <p className="mt-1 text-[#44506B]">{c.description}</p>
                </div>
                <div className="text-right">
                  <div className="flex items-center gap-1 text-[#E8899F] font-bold text-lg">
                    <Flame className="h-5 w-5" /> {streak}
                  </div>
                  <button data-testid={`del-ch-${c.id}`} onClick={() => remove(c.id)} className="mt-1 text-[#77849C] hover:text-red-500">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div className="mt-4 h-2 bg-[#E3E8F1] rounded-full overflow-hidden">
                <div className="h-full bg-[#23375E] transition-all" style={{ width: `${pct}%` }} />
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {days.map((d) => {
                  const done = c.completed_days.includes(d);
                  return (
                    <button key={d} data-testid={`day-${c.id}-${d}`} onClick={() => check(c.id, d)}
                      className={`h-11 w-11 rounded-2xl border font-semibold text-sm transition ${
                        done ? "bg-[#23375E] text-white border-[#23375E]" : "bg-white text-[#44506B] border-[#E3E8F1] hover:border-[#23375E]"
                      }`}>
                      {done ? <CheckCircle2 className="h-4 w-4 mx-auto" /> : `J${d + 1}`}
                    </button>
                  );
                })}
              </div>
              {streak === c.duration_days && (
                <button
                  data-testid={`souvenir-btn-${c.id}`}
                  onClick={() => showSouvenir(c)}
                  className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#FFF4DC] hover:bg-[#F7E7BE] px-5 py-2 text-sm font-semibold text-[#16233D] transition"
                >
                  <Sparkles className="h-4 w-4" /> Voir la carte souvenir
                </button>
              )}
            </article>
          );
        })}
      </section>

      <SouvenirCard
        open={!!souvenirChallenge}
        onOpenChange={(v) => !v && setSouvenirChallenge(null)}
        challenge={souvenirChallenge}
        parentName={user?.name}
      />
    </div>
  );
}
